import { EyeOff } from "lucide-react"
import { Tooltip } from "@/components/ui/tooltip"
import { refusalOfError } from "@/lib/api"
import { formattingTag, useLocale, useT } from "@/lib/i18n"
import { exactText, stringifyExact } from "@/lib/lossless"
import { cn } from "@/lib/utils"
import { HiddenForRole, useLens } from "./lens"
import type { FieldMeta, TypeMeta } from "./ontology"
import { optionLabel } from "./ontology"

/** The cell's masking marker for one property, when the reader may use but not see it. */
export type Masked = Record<string, { markings?: readonly string[] } | unknown> | undefined

export function isMasked(masked: Masked, id: string): boolean {
  return Boolean(masked && id in masked)
}

export function maskMarkings(masked: Masked, id: string): string[] {
  const entry = masked?.[id]
  if (entry && typeof entry === "object" && "markings" in entry && Array.isArray((entry as { markings: unknown }).markings))
    return (entry as { markings: string[] }).markings
  return []
}

/** "Hidden by policy": the cell withheld this value from the reader; its existence is not secret. */
export function HiddenChip({ markings, className }: { markings?: readonly string[]; className?: string }) {
  const t = useT()
  return (
    <Tooltip content={markings?.length ? t("ws.hiddenByPolicyMarkings", { markings: markings.join(", ") }) : t("ws.hiddenByPolicyHelp")}>
      <span
        tabIndex={0}
        className={cn(
          "inline-flex h-[22px] items-center gap-1.5 rounded-full border border-dashed border-border px-2 text-xs text-muted-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/30",
          className,
        )}
      >
        <EyeOff className="size-3" aria-hidden />
        {t("ws.hiddenByPolicy")}
      </span>
    </Tooltip>
  )
}

/** A number's exact decimal text: preserved digits stay exact, never rounded through a double. */
export function numericText(value: unknown): string | null {
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null
  const exact = exactText(value)
  if (exact !== undefined) return exact
  if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim())) return value.trim()
  return null
}

/** Exact decimal division by 10^digits on the text itself (`360000`, 2 → `3600.00`). */
export function shiftDecimal(text: string, digits: number): string {
  if (digits <= 0) return text
  const negative = text.startsWith("-")
  const [whole = "0", fraction = ""] = (negative ? text.slice(1) : text).split(".")
  const padded = whole.padStart(digits + 1, "0")
  const head = padded.slice(0, padded.length - digits).replace(/^0+(?=\d)/, "")
  const result = `${head}.${padded.slice(padded.length - digits)}${fraction}`
  return negative ? `-${result}` : result
}

/** ISO 4217 minor-unit exponent (EGP 2, JPY 0, KWD 3), as the platform's Intl data declares it. */
export function currencyDigits(currency: string | null): number {
  if (!currency) return 2
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    return 2
  }
}

// Intl formats decimal strings exactly (ES2023); the cast only bridges older lib typings.
const exactly = (text: string) => text as unknown as number

export function useFormatters() {
  const { preference } = useLocale()
  const tag = formattingTag(preference)
  return {
    tag,
    money(amount: string, currency: string | null) {
      const digits = currencyDigits(currency)
      try {
        return new Intl.NumberFormat(tag, currency ? { style: "currency", currency, currencyDisplay: "code", maximumFractionDigits: digits, minimumFractionDigits: 0 } : { maximumFractionDigits: 20 }).format(exactly(amount))
      } catch {
        return `${new Intl.NumberFormat(tag, { maximumFractionDigits: 20 }).format(exactly(amount))}${currency ? ` ${currency}` : ""}`
      }
    },
    number(n: number | string) {
      return new Intl.NumberFormat(tag, { maximumFractionDigits: 20 }).format(typeof n === "string" ? exactly(n) : n)
    },
    compact(n: number) {
      return new Intl.NumberFormat(tag, { notation: n >= 10000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(n)
    },
    date(value: string) {
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
      const d = new Date(dateOnly ? `${value}T00:00:00Z` : value)
      if (Number.isNaN(d.getTime())) return value
      return new Intl.DateTimeFormat(tag, dateOnly ? { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" } : { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d)
    },
  }
}

/** The amount in major units (exact decimal text) and its currency, for a money field of a record. */
export function moneyOf(field: FieldMeta, values: Record<string, unknown>, type?: TypeMeta): { amount: string; currency: string | null } | null {
  const text = numericText(values[field.id])
  if (text === null) return null
  const declared = field.currencyField ? values[field.currencyField] : undefined
  const fallback = type?.fields.find((f) => f.id === field.currencyField)?.options
  const currency =
    typeof declared === "string" && /^[A-Z]{3}$/.test(declared)
      ? declared
      : fallback?.length === 1
        ? (fallback[0]?.value ?? null)
        : (field.currencyDefault ?? null)
  return { amount: field.minorUnits ? shiftDecimal(text, currencyDigits(currency)) : text, currency }
}

const TONES: Record<string, string> = {
  positive: "bg-[var(--success)]/12 text-[var(--success)]",
  pending: "bg-[var(--warning)]/12 text-[var(--warning)]",
  negative: "bg-destructive/12 text-destructive",
  neutral: "bg-secondary text-secondary-foreground",
}

/** Tone from the lifecycle's shape when declared; word meaning only as a fallback for undeclared states. */
export function statusTone(value: string, type?: TypeMeta): keyof typeof TONES {
  const lc = type?.lifecycle
  const v = value.toLowerCase()
  if (/cancel|fail|reject|exception|error|void|lost|blocked|overdue|unmatched/.test(v)) return "negative"
  if (lc) {
    if (lc.terminal.includes(value)) return "positive"
    if (lc.initial.includes(value)) return "neutral"
    return "pending"
  }
  if (/unpaid|delayed/.test(v)) return "pending"
  if (/paid|deliver|complete|done|posted|approved|active|current|closed|success|published|healthy|matched/.test(v)) return "positive"
  if (/pending|await|draft|review|reserved|queued|planned|intake|open|progress|scheduled|needs/.test(v)) return "pending"
  return "neutral"
}

export function StatusPill({ value, field, type, className }: { value: string; field?: FieldMeta; type?: TypeMeta; className?: string }) {
  const label = field?.options.find((o) => o.value === value)?.label ?? optionLabel(value)
  const tone = statusTone(value, type)
  return (
    <span className={cn("inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-xs font-medium whitespace-nowrap", TONES[tone], className)}>
      <span aria-hidden className="size-1.5 rounded-full bg-current opacity-80" />
      {label}
    </span>
  )
}

/** One field value, formatted by its ontology role. References render as text here; pages link them. */
export function FieldValue({
  field,
  values,
  masked,
  type,
  className,
}: {
  field: FieldMeta
  values: Record<string, unknown>
  masked?: Masked
  type?: TypeMeta
  className?: string
}) {
  const t = useT()
  const fmt = useFormatters()
  const lens = useLens()
  if (isMasked(masked, field.id)) {
    const markings = maskMarkings(masked, field.id)
    return lens && markings.length ? <HiddenForRole markings={markings} className={className} /> : <HiddenChip markings={markings} className={className} />
  }
  const value = values[field.id]
  if (value === null || value === undefined || value === "") return <span className={cn("text-subtle-foreground", className)}>—</span>
  switch (field.role) {
    case "status":
      return typeof value === "string" ? <StatusPill value={value} field={field} type={type} className={className} /> : <Plain value={value} className={className} />
    case "money": {
      const money = moneyOf(field, values, type)
      if (!money) return <Plain value={value} className={className} />
      return (
        <span dir="ltr" className={cn("tabular-nums", className)}>
          {fmt.money(money.amount, money.currency)}
        </span>
      )
    }
    case "quantity": {
      const n = numericText(value)
      return <span className={cn("tabular-nums", className)}>{n === null ? String(value) : fmt.number(n)}</span>
    }
    case "date":
      return typeof value === "string" ? <span className={cn("tabular-nums", className)}>{fmt.date(value)}</span> : <Plain value={value} className={className} />
    case "flag":
      return <span className={className}>{value === true ? t("common.yes") : value === false ? t("common.no") : String(value)}</span>
    case "identifier":
    case "reference":
      return (
        <span dir="ltr" className={className}>
          {String(exactText(value) ?? value)}
        </span>
      )
    default: {
      if (typeof value === "string" && field.options.length) {
        const opt = field.options.find((o) => o.value === value)
        return <span className={className}>{opt?.label ?? value}</span>
      }
      return <Plain value={value} className={className} />
    }
  }
}

function Plain({ value, className }: { value: unknown; className?: string }) {
  const fmt = useFormatters()
  if (typeof value === "string") return <span dir="auto" className={cn("break-words", className)}>{value}</span>
  const n = numericText(value)
  if (n !== null) return <span className={cn("tabular-nums", className)}>{fmt.number(n)}</span>
  if (typeof value === "boolean") return <span className={className}>{String(value)}</span>
  return <code dir="ltr" className={cn("font-mono text-2xs break-all text-muted-foreground", className)}>{stringifyExact(value)}</code>
}

/**
 * A refused read, stated quietly but completely: the human sentence, then the kernel's code,
 * category and explanation (the console never hides why the cell said no).
 */
export function RefusalNote({ error, title, className }: { error: unknown; title: string; className?: string }) {
  const refused = refusalOfError(error)
  return (
    <div role="alert" className={cn("flex items-start gap-2.5 rounded-xl border border-dashed border-border px-3.5 py-3 text-sm", className)}>
      <EyeOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="text-foreground/90">{title}</p>
        {refused ? (
          <>
            <p className="text-xs text-muted-foreground">{refused.refusal.explanation}</p>
            <p dir="ltr" className="font-mono text-2xs text-subtle-foreground">
              {refused.refusal.code} · {refused.refusal.category}
            </p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        )}
      </div>
    </div>
  )
}

/** Several refused reads (previews, references) summarized once per distinct kernel reason. */
export function RefusalSummary({ errors, title, className }: { errors: readonly unknown[]; title: string; className?: string }) {
  const distinct = new Map<string, unknown>()
  for (const error of errors) {
    const refused = refusalOfError(error)
    const key = refused ? `${refused.refusal.code}\u0000${refused.refusal.explanation}` : error instanceof Error ? error.message : String(error)
    if (!distinct.has(key)) distinct.set(key, error)
  }
  if (!distinct.size) return null
  return (
    <div className={cn("space-y-2", className)}>
      {[...distinct.values()].map((error, i) => (
        <RefusalNote key={i} error={error} title={title} />
      ))}
    </div>
  )
}
